import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
} from "@tanstack/react-query";
import { TApiServiceError, TApiServiceOptions } from "../apiService";
import { TBrandingDetailsResponse, TBrandingFormData } from "./branding.types";
import { brandingService } from "./branding.service";
import { buildQueryParams } from "../../utils/commonUtils";

const GET_BRANDING_ACCOUNTS_QUERY_KEY = "brandingAccounts";
const GET_THEME_QUERY_KEY = "GET_THEME";

/**
 * Custom hook to fetch branding details using React Query.
 *
 * @param params - A record of key-value pairs representing query parameters.
 * @param options - Optional configuration for the React Query `useQuery` hook, excluding `queryKey` and `queryFn`.
 * @returns The result of the `useQuery` hook for fetching branding details.
 */
export const useGetBrandingListQuery = (
  params: Record<string, string | number | boolean | undefined | null>,
  options?: Omit<
    UseQueryOptions<TBrandingDetailsResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: [GET_BRANDING_ACCOUNTS_QUERY_KEY, "branding_list", params],
    queryFn: async () => {
      const query = buildQueryParams(params);
      return await brandingService.getBrandingList({
        searchParams: Object.keys(params).length ? query : undefined,
      });
    },
    ...options,
  });
};

// Utility to convert TBrandingFormData to FormData
const toFormData = (payload: TBrandingFormData): FormData => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Always treat logo_main, logo_secondary, favicon as files if present
      if (
        [
          "logo_main_light",
          "logo_secondary_light",
          "favicon_light",
          "logo_main_dark",
          "logo_secondary_dark",
          "favicon_dark",
        ].includes(key)
      ) {
        if (typeof File !== "undefined" && value instanceof File) {
          formData.append(key, value);
        }
        // If not a File, skip appending
      } else if (
        (typeof File !== "undefined" && value instanceof File) ||
        (typeof Blob !== "undefined" && value instanceof Blob)
      ) {
        formData.append(key, value);
      } else {
        formData.append(key, value);
      }
    }
  });
  return formData;
};

/**
 * Custom hook to add branding details using React Query mutation.
 *
 * @param options - Optional configuration for the React Query `useMutation` hook, excluding `mutationFn`.
 * @returns The result of the `useMutation` hook for adding branding.
 */
export const usePostBrandingMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TBrandingFormData>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (payload: TBrandingFormData) => {
      try {
        const formData = toFormData(payload);
        const response = await brandingService.addBranding(formData);
        return response;
      } catch (error) {
        throw error as TApiServiceError;
      }
    },
    ...options,
  });
};

/**
 * Custom hook to update branding details using React Query mutation.
 *
 * @param options - Optional configuration for the React Query `useMutation` hook, excluding `mutationFn`.
 * @returns The result of the `useMutation` hook for updating branding.
 */
export const usePutBrandingMutation = (
  options?: Omit<
    UseMutationOptions<any, TApiServiceError, TBrandingFormData>,
    "mutationFn"
  >,
) => {
  return useMutation({
    mutationFn: async (payload: TBrandingFormData) => {
      try {
        const formData = toFormData(payload);
        const response = await brandingService.updateBranding(formData);
        return response;
      } catch (error) {
        throw error as TApiServiceError;
      }
    },
    ...options,
  });
};

/**
 * Custom hook to fetch theme details using React Query.
 *
 * @param serviceOptions - Optional configuration for the API service call.
 * @param options - Optional configuration for the React Query `useQuery` hook, excluding `queryKey` and `queryFn`.
 * @returns The result of the `useQuery` hook for fetching theme details.
 */
export const useGetThemeQuery = (
  serviceOptions?: TApiServiceOptions,
  options?: Omit<
    UseQueryOptions<TBrandingDetailsResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: [GET_THEME_QUERY_KEY, serviceOptions],
    queryFn: async () => {
      try {
        return await brandingService.getTheme(serviceOptions);
      } catch (error) {
        throw error as TApiServiceError;
      }
    },
    ...options,
  });
};
